// «Справочники» на вкладке «Работы» (владелец 2026-10-17): два списка рядом — слева работы текущего проекта,
// справа общий справочник (проект «Глобально»). Отмеченные работы переносятся стрелками: «→» — в справочник
// (POST /works/to-global), «←» — в проект (POST /works/from-global, с задачами по раскатке). Одноимённые на
// другой стороне не перезаписываются: такие работы серые, с пометкой «есть».
import { useCallback, useEffect, useState } from 'react';
import { call, p, type Data } from './api';
import { Pop, type Anchor } from './Pop';
import type { ShellCtx } from './shell';

type Work = Data<'/api/projects/{pid}/works', 'get'>[number];
type Project = Data<'/api/projects', 'get'>[number];

function Spisok({ zag, raboty, est, sel, setSel }: {
  zag: string; raboty: Work[] | null; est: Set<string>; sel: number[]; setSel: (f: (s: number[]) => number[]) => void;
}) {
  const [q, setQ] = useState('');
  const vidny = (raboty || []).filter(w => !q || w.name.toLowerCase().includes(q.toLowerCase()));
  const poEtapu = new Map<string, Work[]>();
  vidny.forEach(w => { const k = (w.etap || '').trim() || 'Без этапа'; poEtapu.set(k, [...(poEtapu.get(k) || []), w]); });
  const etapy = [...poEtapu.keys()].sort((a, b) => (a === 'Без этапа' ? 1 : b === 'Без этапа' ? -1 : a.localeCompare(b, 'ru', { numeric: true })));
  const svob = vidny.filter(w => !est.has(w.name.toLowerCase())).map(w => w.id);
  const vse = svob.length > 0 && svob.every(id => sel.includes(id));
  return (
    <div className="spr-kol">
      <div className="spr-zag">{zag} <span className="muted">{raboty ? raboty.length : ''}</span></div>
      <div className="spr-instr">
        <input placeholder="Поиск по названию…" value={q} onChange={e => setQ(e.target.value)} />
        <label title="Отметить все видимые работы, которых нет на другой стороне">
          <input type="checkbox" checked={vse} disabled={!svob.length}
            onChange={e => setSel(s => (e.target.checked ? [...new Set([...s, ...svob])] : s.filter(x => !svob.includes(x))))} /> все
        </label>
      </div>
      <div className="spr-list">
        {raboty === null ? <div className="muted">Загрузка…</div> : !vidny.length ? <div className="muted">Ничего нет.</div>
          : etapy.map(et => {
            const gr = poEtapu.get(et)!;
            const sv = gr.filter(w => !est.has(w.name.toLowerCase())).map(w => w.id);
            const vseEt = sv.length > 0 && sv.every(id => sel.includes(id));
            return (
              <div key={et} className="spr-etap">
                <label className="spr-etap-zag" title="Отметить все работы этапа">
                  <input type="checkbox" checked={vseEt} disabled={!sv.length}
                    onChange={e => setSel(s => (e.target.checked ? [...new Set([...s, ...sv])] : s.filter(x => !sv.includes(x))))} />
                  {et}
                </label>
                {gr.map(w => {
                  const uzhe = est.has(w.name.toLowerCase());
                  return (
                    <label key={w.id} className={'spr-rab' + (uzhe ? ' uzhe' : '')}
                      title={uzhe ? 'Работа с таким названием уже есть на другой стороне' : undefined}>
                      <input type="checkbox" disabled={uzhe} checked={sel.includes(w.id)}
                        onChange={e => setSel(s => (e.target.checked ? [...s, w.id] : s.filter(x => x !== w.id)))} />
                      <span>{w.name}</span>{uzhe && <span className="spr-est">есть</span>}
                    </label>);
                })}
              </div>);
          })}
      </div>
    </div>
  );
}

export function Spravochniki({ ctx, at, onClose }: { ctx: ShellCtx; at: Anchor; onClose: () => void }) {
  const [gpid, setGpid] = useState<number | null>(null);
  const [moi, setMoi] = useState<Work[] | null>(null);
  const [obshchie, setObshchie] = useState<Work[] | null>(null);
  const [selL, setSelL] = useState<number[]>([]);
  const [selR, setSelR] = useState<number[]>([]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const pr = await call<Project[]>('get', '/api/projects');
    if (!pr.ok) { setNote(pr.error.message); return; }
    const gp = pr.data.find(x => x.code === 'global');
    if (!gp) { setNote('Проекта «Глобально» нет — общий справочник недоступен.'); return; }
    if (gp.id === ctx.pid) { setNote('Это и есть общий справочник «Глобально» — работы заводятся здесь напрямую.'); return; }
    setGpid(gp.id);
    const [a, b] = await Promise.all([
      call<Work[]>('get', p('/api/projects/{pid}/works', { pid: ctx.pid })),
      call<Work[]>('get', p('/api/projects/{pid}/works', { pid: gp.id })),
    ]);
    if (a.ok) setMoi(a.data); else setNote(a.error.message);
    if (b.ok) setObshchie(b.data); else setNote(b.error.message);
  }, [ctx.pid]);
  useEffect(() => { void load(); }, [load]);

  const imenaMoi = new Set((moi || []).map(w => w.name.toLowerCase()));
  const imenaObsh = new Set((obshchie || []).map(w => w.name.toLowerCase()));

  async function vSpravochnik() {
    if (!selL.length) { setNote('Отметьте слева работы проекта, которые нужно добавить в справочник.'); return; }
    setBusy(true);
    const r = await call<{ dobavleno: number; uzhe_byli: string[]; svyazej: number }>('post',
      p('/api/projects/{pid}/works/to-global', { pid: ctx.pid }), { ids: selL });
    setBusy(false);
    if (!r.ok) { setNote(r.error.message); return; }
    setNote('В справочник добавлено работ: ' + r.data.dobavleno + (r.data.svyazej ? ', связей: ' + r.data.svyazej : '')
      + (r.data.uzhe_byli.length ? '. Уже были, пропущены: ' + r.data.uzhe_byli.join(', ') : '') + '.');
    setSelL([]); await load();
  }
  async function vProekt() {
    if (!selR.length) { setNote('Отметьте справа работы справочника, которые нужно добавить в проект.'); return; }
    setBusy(true);
    const r = await call<{ dobavleno: number; sozdano_zadach: number; uzhe_byli: string[]; svyazej: number }>('post',
      p('/api/projects/{pid}/works/from-global', { pid: ctx.pid }), { ids: selR });
    setBusy(false);
    if (!r.ok) { setNote(r.error.message); return; }
    setNote('В проект добавлено работ: ' + r.data.dobavleno + (r.data.svyazej ? ', связей: ' + r.data.svyazej : '') + (r.data.sozdano_zadach ? ', создано задач: ' + r.data.sozdano_zadach : '')
      + (r.data.uzhe_byli.length ? '. Уже были, пропущены: ' + r.data.uzhe_byli.join(', ') : '')
      + (r.data.sozdano_zadach ? '. Даты — после «Пересчитать» в «Планировании».' : '.'));
    setSelR([]); await load();
    ctx.onChanged?.();
  }

  /** связи между работами, которые есть с обеих сторон (владелец 2026-10-17: «импорт со связями») */
  async function svyazi(napr: 'v_proekt' | 'v_spravochnik') {
    setBusy(true);
    const r = await call<{ svyazej: number }>('post', p('/api/projects/{pid}/svyazi-spravochnik', { pid: ctx.pid }), { napr });
    setBusy(false);
    if (!r.ok) { setNote(r.error.message); return; }
    setNote((napr === 'v_proekt' ? 'В проект' : 'В справочник') + ' добавлено связей: ' + r.data.svyazej
      + (r.data.svyazej ? '' : ' — все связи общих работ уже есть')
      + (napr === 'v_proekt' && r.data.svyazej ? '. Даты — после «Пересчитать» в «Планировании».' : '.'));
    if (napr === 'v_proekt' && r.data.svyazej) ctx.onChanged?.();
  }

  return (
    <Pop at={at} title="Справочники работ" onClose={onClose} width={980}>
      <div className="spr-okno">
        <Spisok zag="Текущий проект" raboty={moi} est={imenaObsh} sel={selL} setSel={setSelL} />
        <div className="spr-strelki">
          <button disabled={busy || !gpid || !selL.length} title="Добавить отмеченные работы проекта в общий справочник"
            onClick={() => void vSpravochnik()}>В справочник&nbsp;→{selL.length ? <><br />{selL.length}</> : null}</button>
          <button disabled={busy || !gpid || !selR.length} title="Добавить отмеченные работы справочника в проект (с задачами по раскатке)"
            onClick={() => void vProekt()}>←&nbsp;В проект{selR.length ? <><br />{selR.length}</> : null}</button>
          <span className="spr-sv-zag">Связи</span>
          <button className="sec" disabled={busy || !gpid} title="Связи между работами, которые есть и в проекте, и в справочнике: недостающие — в справочник"
            onClick={() => void svyazi('v_spravochnik')}>Связи →</button>
          <button className="sec" disabled={busy || !gpid} title="Связи между работами, которые есть и в проекте, и в справочнике: недостающие — в проект"
            onClick={() => void svyazi('v_proekt')}>← Связи</button>
        </div>
        <Spisok zag="Общий справочник («Глобально»)" raboty={obshchie} est={imenaMoi} sel={selR} setSel={setSelR} />
      </div>
      {/* как пользоваться (владелец 2026-10-17: «не понимаю, как добавить работы в проект») */}
      <div className="spr-note">Отметьте галочками работы <b>справа</b> и нажмите «← В проект» — они добавятся в проект.
        Отметьте <b>слева</b> и нажмите «В справочник →» — уйдут в общий справочник. Работы переносятся вместе со связями.
        Серые с пометкой «есть» уже есть на другой стороне; их связи — кнопками «Связи →» и «← Связи».</div>
      {note && <div className="spr-note spr-itog">{note}</div>}
    </Pop>
  );
}
